import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, Save, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || '';

export const ContactModal = ({ open, onOpenChange, contactInfo, setContactInfo, onSave, saving }) => {
  const [teamContacts, setTeamContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  useEffect(() => {
    if (open) {
      loadTeamContacts();
    }
  }, [open]);

  const loadTeamContacts = async () => {
    try {
      setLoadingContacts(true);
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const response = await axios.get(`${API}/api/team-contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setTeamContacts(response.data.contacts || []);
      }
    } catch (err) {
      console.error('Error loading team contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleSelectContact = (contact) => {
    setContactInfo({
      name: contact.name || '',
      title: contact.role || '',
      phone: contact.phone || '',
      email: contact.email || '',
      address: contact.office_address || contactInfo.address || ''
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-lg">
        <DialogHeader>
          <DialogTitle>Coordonnées du conseiller</DialogTitle>
          <DialogDescription>
            Ces informations apparaîtront sur la dernière page du PDF.
          </DialogDescription>
        </DialogHeader>

        {/* Team contacts dropdown */}
        {teamContacts.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between" data-testid="contact-dropdown-trigger">
                <span className="text-sm text-muted-foreground truncate">
                  {loadingContacts ? 'Chargement...' : `Sélectionner un membre de l'équipe (${teamContacts.length})`}
                </span>
                <ChevronDown className="w-4 h-4 ml-2 opacity-50 flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]" align="start">
              {teamContacts.map((contact) => (
                <DropdownMenuItem
                  key={contact.id}
                  onClick={() => handleSelectContact(contact)}
                  className="flex items-center justify-between cursor-pointer py-2.5"
                  data-testid={`contact-option-${contact.id}`}
                >
                  <div className="min-w-0">
                    <span className="font-medium text-sm block truncate">{contact.name}</span>
                    <span className="text-xs text-muted-foreground block truncate">{contact.email}</span>
                  </div>
                  <Badge variant="secondary" className="ml-2 text-[10px] flex-shrink-0">{contact.role}</Badge>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Contact form fields */}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="contact-name">Nom complet</Label>
            <Input
              id="contact-name"
              data-testid="contact-name-input"
              placeholder="Prénom NOM"
              value={contactInfo.name}
              onChange={(e) => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact-title">Titre / Fonction</Label>
            <Input
              id="contact-title"
              data-testid="contact-title-input"
              placeholder="Chargé(e) d'affaires"
              value={contactInfo.title}
              onChange={(e) => setContactInfo(prev => ({ ...prev, title: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone">Téléphone</Label>
              <Input
                id="contact-phone"
                data-testid="contact-phone-input"
                placeholder="06 XX XX XX XX"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">E-mail</Label>
              <Input
                id="contact-email"
                data-testid="contact-email-input"
                placeholder="email@pde.com"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact-address">Adresse bureau</Label>
            <Input
              id="contact-address"
              data-testid="contact-address-input"
              placeholder="1 Bd Sainte-Barbe, 59140 Dunkerque"
              value={contactInfo.address}
              onChange={(e) => setContactInfo(prev => ({ ...prev, address: e.target.value }))}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={onSave} disabled={saving} data-testid="save-contact-btn">
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Appliquer au rapport
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
